import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const Footer = () => {
  const { t, lang } = useLanguage();

  const quickLinks = [
    { id: "about", label: t.nav.about },
    { id: "specialties", label: t.nav.specialties },
    { id: "pathologies", label: "Pathologies" },
    { id: "raac", label: lang === "fr" ? "Protocole RAAC" : "Recovery protocol" },
    { id: "sport", label: t.nav.sport },
    { id: "locations", label: t.nav.locations },
    { id: "expertise", label: t.nav.expertise },
    { id: "media", label: lang === "fr" ? "Conférences" : "Lectures" },
    { id: "blog", label: t.nav.blog },
    { id: "faq", label: t.nav.faq },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A1428] text-white pt-16 pb-8 relative"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src={SITE.logo} alt="IMOTEpS" className="h-14 w-auto" />
              <div>
                <p className="font-display font-bold text-white">IMOTEpS</p>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">
                  Dr Richard Aswad — Marseille
                </p>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/70 leading-relaxed max-w-md">
              {t.footer.about}
            </p>

            {/* SEO — Pôles d'expertise (rich internal linking, no keyword stuffing) */}
            <div className="mt-7">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                {t.footer.expertiseTitle}
              </p>
              <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
                {t.footer.expertiseLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      data-testid={`footer-seo-link-${link.href.replace(/\//g, "-")}`}
                      className="inline-block text-xs text-white/65 hover:text-[#FF5A1F] transition-colors border-b border-white/10 hover:border-[#FF5A1F] pb-0.5"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] text-white/45 leading-relaxed max-w-md italic">
                {t.footer.seoTagline}
              </p>
            </div>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-1.5 text-xs font-semibold text-[#FF5A1F]">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              {t.footer.emergency}
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {t.footer.quickLinks}
            </p>
            <ul className="mt-4 space-y-2.5">
              {quickLinks.map((l) => (
                <li key={l.id}>
                  <Link
                    to={`/#${l.id}`}
                    data-testid={`footer-link-${l.id}`}
                    className="text-sm text-white/75 hover:text-[#FF5A1F] transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/publications"
                  data-testid="footer-link-publications"
                  className="text-sm text-white/75 hover:text-[#FF5A1F] transition-colors"
                >
                  {lang === "fr" ? "Publications" : "Publications"}
                </Link>
              </li>
              <li>
                <Link
                  to="/recherche"
                  data-testid="footer-link-recherche"
                  className="text-sm text-white/75 hover:text-[#FF5A1F] transition-colors"
                >
                  {lang === "fr" ? "Centre bibliographique" : "Bibliography center"}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {t.footer.contact}
            </p>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-[#FF5A1F] mt-1" aria-hidden="true"></i>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Clinique+Juge+118+Rue+Jean+Mermoz+13008+Marseille"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-address-maps"
                  className="text-white/75 hover:text-[#FF5A1F] transition group"
                >
                  {SITE.address}
                  <span className="block text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5 group-hover:text-[#FF5A1F]/70">
                    {lang === "fr" ? "Voir sur Google Maps" : "View on Google Maps"}
                    <i className="fa-solid fa-arrow-up-right-from-square ml-1.5 text-[9px]" aria-hidden="true"></i>
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-[#FF5A1F]" aria-hidden="true"></i>
                <a href={SITE.phoneHref} className="text-white/75 hover:text-[#FF5A1F] transition">
                  {SITE.phone}
                  <span className="block text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5">
                    {lang === "fr" ? "Prise de rendez-vous" : "Appointment booking"}
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-user-tie text-[#FF5A1F]" aria-hidden="true"></i>
                <a href={SITE.assistantPhoneHref} className="text-white/75 hover:text-[#FF5A1F] transition">
                  {SITE.assistantPhone}
                  <span className="block text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5">
                    {lang === "fr" ? `Assistante — ${SITE.assistantName}` : `Assistant — ${SITE.assistantName}`}
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#FF5A1F]" aria-hidden="true"></i>
                <a href={SITE.assistantEmailHref} className="text-white/75 hover:text-[#FF5A1F] transition break-all text-xs">
                  {SITE.assistantEmail}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#FF5A1F]" aria-hidden="true"></i>
                <a href={SITE.emailHref} className="text-white/75 hover:text-[#FF5A1F] transition">
                  {SITE.email}
                  <span className="block text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5">
                    {lang === "fr" ? "Email Dr Aswad" : "Dr Aswad's email"}
                  </span>
                </a>
              </li>
            </ul>

            <p className="mt-8 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
              {t.footer.legal}
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs">
              {t.footer.legalLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-white/60 hover:text-[#FF5A1F] transition"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs text-white/50">{t.footer.copyright}</p>
          <p className="text-xs text-white/40">
            Fait à Marseille — avec rigueur & précision.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
